/**
 * Capa 2 (V96) — Configuración → "Facturar a mis clientes" (solo SUPER_USER).
 * Onboarding del tenant como EMISOR CFDI en 3 pasos:
 *   1. Identidad fiscal (RFC/razón/régimen/CP/serie) → persona en el PAC.
 *   2. CSD (.cer + .key + contraseña) → PASS-THROUGH al PAC (nunca se guarda aquí).
 *   3. Manifiesto: firma con la FIEL (e.firma) → PASS-THROUGH; el PDF firmado
 *      queda como evidencia.
 * La activación final (candado issuer_enabled) la hace FluidSol tras el gate
 * legal (contrato + aviso de privacidad del tenant): el tenant ve el estatus.
 * AUTO-GATEADA: si el backend dice enabled=false (kill-switch global) o el rol
 * no es SUPER_USER, no se renderiza (feature dormida).
 */
import { useEffect, useState } from 'react';
import { bookingInvoiceButton, showBookingInvoice, showInvoiceToggle } from './invoiceToggles';
import { API_BASE, api, getToken } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { REGIMEN_LABELS } from './FiscalDataFields';
import { ContractSection5 } from './ContractSection5';
import { FoliosUsageMeter } from './FoliosUsageMeter';
import { describeMissing } from '../auth/cfdiNotice';

/** V222 (C-02): la hora de emisión de la global va de 0 a 20 (deja reintentos en la ventana de 24 h). */
const GLOBAL_MAX_RUN_HOUR = 20;

interface IssuerStatus {
  enabled: boolean;
  configured: boolean;
  ready: boolean;
  data: {
    rfc: string; razonSocial: string; regimenFiscal: string; cp: string; serie: string;
    personLinked: boolean; csdStatus: string; csdValidTo: string | null; csdVigente: boolean;
    manifestStatus: string; issuerEnabled: boolean; cfdiModules: string[];
    // V98 — gate legal (contrato Capa 2 + attestation del aviso de privacidad)
    contractSignedAt: string | null; contractVersion: string | null; contractCurrentVersion: string;
    // V199 — TRUE si la aceptación es de la versión VIGENTE (una anterior no cuenta).
    contractCurrent: boolean;
    privacyAttestedAt: string | null; privacyNoticeVersion: string | null; privacyNoticeUrl: string | null;
    // V110/V113 (D) — factura global: opt-in + evidencia del ack + periodicidad + hora.
    globalInvoiceEnabled: boolean; globalInvoiceNoticeAckAt: string | null; globalInvoiceRunHour: number | null;
    globalInvoicePeriodicity: string; globalInvoiceRunWeekday: number | null;
    // V222 (C-03) — lo que le falta al emisor para timbrar (vacío = listo).
    issuerMissing?: string[];
    // V118 — "facturar al reservar": intención + compuerta fail-closed (emisor listo + Pagos activo).
    bookingInvoiceEnabled: boolean; chargesEnabled: boolean; bookingInvoiceGate: boolean;
    /** V223 (R-01): false en GastroGest — sus reservas no se cobran. */
    bookingInvoiceAvailable?: boolean;
    // PR A (V204) — aviso de consumo de folios aceptado por toggle (gate "aviso al activar").
    folioNoticeGlobalAccepted: boolean; folioNoticeBookingAccepted: boolean;
    // V111 (F) — aviso de privacidad hospedado por FluidSol.
    privacyNoticeHosted: boolean; privacyNoticeHostedVersion: string | null; privacyNoticeHostedAt: string | null;
  } | null;
  catalogs: { regimenesFiscales: string[] };
}

const errMsg = (e: any, fallback: string): string => {
  const msgs = e?.body?.message;
  return Array.isArray(msgs) ? msgs.join(' ') : msgs || e?.message || fallback;
};

/** Multipart .cer/.key + password con Bearer (apiUpload solo maneja 1 archivo). */
async function uploadCertPair(path: string, cer: File, key: File, password: string) {
  const form = new FormData();
  form.append('cer', cer);
  form.append('key', key);
  form.append('password', password);
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    body: form,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) throw { status: res.status, body: data, message: data?.message };
  return data;
}

function Chip({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium ${
      ok ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
    }`}>
      <span className={`h-1.5 w-1.5 rounded-full ${ok ? 'bg-emerald-500' : 'bg-slate-400'}`} />
      {label}
    </span>
  );
}

/** Paso 2 (CSD). Feedback INLINE prop-driven (error/okMsg): antes el error/éxito
 *  vivía al FONDO de la card (lejos del Paso 2) → el tenant no lo veía (#568). */
export function CertPairInputs({ label, hint, onSubmit, busy, cta, error, okMsg }: {
  label: string; hint: string; cta: string; busy: boolean;
  error?: string | null; okMsg?: string | null;
  onSubmit: (cer: File, key: File, password: string) => void;
}) {
  const [cer, setCer] = useState<File | null>(null);
  const [key, setKey] = useState<File | null>(null);
  const [password, setPassword] = useState('');
  const inputCls = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-400 focus:outline-none';
  const labelCls = 'text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400';
  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <p className="mb-1 text-sm font-medium text-slate-800">{label}</p>
      <p className="mb-3 text-xs text-slate-500">{hint}</p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div>
          <label className={labelCls}>Certificado (.cer)</label>
          <input type="file" accept=".cer" disabled={busy} className="block w-full text-xs text-slate-600 disabled:opacity-50"
            onChange={(e) => setCer(e.target.files?.[0] ?? null)} />
        </div>
        <div>
          <label className={labelCls}>Llave privada (.key)</label>
          <input type="file" accept=".key" disabled={busy} className="block w-full text-xs text-slate-600 disabled:opacity-50"
            onChange={(e) => setKey(e.target.files?.[0] ?? null)} />
        </div>
        <div>
          <label className={labelCls}>Contraseña</label>
          <input type="password" value={password} disabled={busy} onChange={(e) => setPassword(e.target.value)}
            autoComplete="off" className={inputCls} />
        </div>
      </div>
      {/* Feedback INLINE del paso (la causa en palabras del tenant / la confirmación). */}
      {error && <p className="mt-3 text-sm text-rose-600">{error}</p>}
      {okMsg && <p className="mt-3 text-sm font-medium text-emerald-700">{okMsg}</p>}
      <div className="mt-3 flex justify-end">
        <button
          onClick={() => cer && key && password && onSubmit(cer, key, password)}
          disabled={busy || !cer || !key || !password}
          className="rounded-xl bg-[var(--brand-primary)] px-3 py-2 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
          {busy ? 'Enviando al PAC…' : cta}
        </button>
      </div>
    </div>
  );
}

/** Mensaje de éxito del Paso 2 (con la vigencia si el .cer parseó). Puro = testeable. */
export function csdOkMessage(validTo?: string | null): string {
  if (!validTo) return 'CSD cargado y custodiado por el PAC ✓';
  const d = new Date(validTo);
  return Number.isNaN(d.getTime())
    ? 'CSD cargado y custodiado por el PAC ✓'
    : `CSD cargado · vigente hasta ${d.toLocaleDateString('es-MX')} ✓`;
}

/**
 * V98 — gate legal del emisor: (4) aceptar el contrato de Capa 2 (queda en el
 * registro permanente legal_acceptances; texto placeholder hasta que la
 * abogada lo entregue) y (5) atestar que el aviso de privacidad del TENANT
 * declara la finalidad "datos de facturación" y nombra a FluidSol como
 * sistema externo. FluidSol valida por estatus y solo entonces activa.
 */
function LegalGateSection({ d, slug, tenantName, run, busy }: {
  d: NonNullable<IssuerStatus['data']>;
  slug: string;
  tenantName: string;
  run: (fn: () => Promise<void>) => Promise<void>;
  busy: boolean;
}) {
  const [contractChecked, setContractChecked] = useState(false);
  const [noticeChecked, setNoticeChecked] = useState(false);
  // Prefill desde el aviso hospedado si ya se publicó (URL pública compuesta al
  // vuelo desde el host del ambiente, NO persistida). Publicar/gestionar/descargar
  // el aviso vive ahora en Configuración › Usuarios, acceso y legal (un solo
  // lugar); aquí el emisor CFDI solo ATESTA que su aviso declara la finalidad fiscal.
  const hostedUrl = d.privacyNoticeHosted && slug ? `${window.location.origin}/aviso/${slug}` : '';
  const [noticeVersion, setNoticeVersion] = useState(d.privacyNoticeHosted ? (d.privacyNoticeHostedVersion || '') : '');
  const [noticeUrl, setNoticeUrl] = useState(hostedUrl);
  const inputCls = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-400 focus:outline-none';
  const labelCls = 'text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400';

  return (
    <>
      {/* Paso 4 — contrato = §5 de los TyC (version-aware: una aceptación de
          versión anterior NO cuenta; al cambiar la versión, hay que re-aceptar). */}
      <div className="rounded-xl border border-slate-200 p-4">
        <p className="mb-1 text-sm font-medium text-slate-800">Paso 4 · Contrato de facturación a clientes</p>
        {d.contractCurrent ? (
          <p className="text-sm text-emerald-700">
            ✓ Aceptado el {d.contractSignedAt ? new Date(d.contractSignedAt).toLocaleDateString('es-MX') : ''} (versión vigente {d.contractVersion}).
          </p>
        ) : (
          <>
            {d.contractSignedAt && (
              <p className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Aceptaste la versión <strong>{d.contractVersion}</strong> el{' '}
                {new Date(d.contractSignedAt).toLocaleDateString('es-MX')}. El contrato se actualizó a la
                versión vigente <strong>{d.contractCurrentVersion}</strong>; acéptalo de nuevo para continuar.
              </p>
            )}
            <p className="mb-3 text-xs text-slate-500">
              El contrato es la <a href="/legal/terminos-y-condiciones" target="_blank" rel="noopener noreferrer"
                className="underline">§5 «Facturación electrónica (CFDI)» de los Términos y Condiciones</a>:
              tú eres el emisor fiscal responsable, FluidSol es el encargado técnico del timbrado y no
              custodia tu CSD/FIEL. Queda registrado quién lo aceptó, cuándo y en qué versión.
            </p>
            {/* §5 verbatim desde la fuente única servida (no copiado a mano). */}
            <ContractSection5 version={d.contractCurrentVersion} />
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={contractChecked} onChange={(e) => setContractChecked(e.target.checked)}
                className="mt-0.5" />
              <span>
                He leído y acepto el contrato de facturación (TyC §5, versión {d.contractCurrentVersion}).
                Confirmo que soy el emisor fiscal responsable de mis CFDI y que firmaré mi Carta Manifiesto.
              </span>
            </label>
            <div className="mt-3 flex justify-end">
              <button
                onClick={() => run(async () => { await api('/me/cfdi-issuer/contract/accept', { method: 'POST', body: {} }); })}
                disabled={busy || !contractChecked}
                className="rounded-xl bg-[var(--brand-primary)] px-3 py-2 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
                {busy ? 'Registrando…' : (d.contractSignedAt ? 'Re-aceptar contrato' : 'Aceptar contrato')}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Paso 5 — aviso de privacidad: hospedar con FluidSol (F) + attestation */}
      <div className="rounded-xl border border-slate-200 p-4">
        <p className="mb-1 text-sm font-medium text-slate-800">Paso 5 · Tu aviso de privacidad</p>
        {d.privacyAttestedAt ? (
          <>
            <p className="text-sm text-emerald-700">
              ✓ Confirmado el {new Date(d.privacyAttestedAt).toLocaleDateString('es-MX')}
              {d.privacyNoticeVersion ? ` (versión ${d.privacyNoticeVersion})` : ''}
              {d.privacyNoticeUrl ? ' · ' : ''}
              {d.privacyNoticeUrl && (
                <a href={d.privacyNoticeUrl} target="_blank" rel="noreferrer" className="underline">ver aviso</a>
              )}
            </p>
            <p className="mt-2 text-xs text-slate-500">
              Publica, comparte (QR) o actualiza tu aviso en <strong>Configuración › Usuarios, acceso y legal</strong>.
            </p>
          </>
        ) : (
          <>
            <p className="mb-3 text-xs text-slate-500">
              La ley exige que TU aviso de privacidad informe a tus clientes que recabas sus datos para
              facturación y que FluidSol participa como sistema externo. Confírmalo aquí.
            </p>

            {/* La plantilla y la publicación (link + QR) viven en un solo lugar. */}
            <p className="mb-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
              ¿Necesitas tu aviso? Descarga la plantilla o publícalo con FluidSol (link + QR) en{' '}
              <strong>Configuración › Usuarios, acceso y legal</strong>. Si ya lo publicaste, aquí abajo queda
              listo para confirmarlo.
            </p>

            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={noticeChecked} onChange={(e) => setNoticeChecked(e.target.checked)}
                className="mt-0.5" />
              <span>
                Confirmo que mi aviso de privacidad <strong>declara la finalidad de datos de facturación</strong> y
                <strong> nombra a FluidSol</strong> como sistema externo de facturación.
              </span>
            </label>
            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className={labelCls}>Versión o fecha de tu aviso</label>
                <input value={noticeVersion} onChange={(e) => setNoticeVersion(e.target.value)} maxLength={50}
                  placeholder="p. ej. v2 / 2026-07" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Link a tu aviso (opcional)</label>
                <input value={noticeUrl} onChange={(e) => setNoticeUrl(e.target.value)} maxLength={500}
                  placeholder="https://…" className={inputCls} />
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <button
                onClick={() => run(async () => {
                  await api('/me/cfdi-issuer/privacy-attestation', {
                    method: 'POST',
                    body: { confirmed: true, noticeVersion: noticeVersion.trim(), noticeUrl: noticeUrl.trim() || undefined },
                  });
                })}
                disabled={busy || !noticeChecked || !noticeVersion.trim()}
                className="rounded-xl bg-[var(--brand-primary)] px-3 py-2 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
                {busy ? 'Registrando…' : 'Confirmar aviso'}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

/**
 * PR2/D — opt-in de la FACTURA GLOBAL diaria (público en general): al cierre del
 * día, las ventas NO facturadas individualmente se consolidan en UN CFDI global.
 * Requiere reconocer el aviso ("se generará una factura global diaria") — la
 * evidencia queda en global_invoice_notice_ack_at. La hora de corrida es una
 * cota del cron (NULL = automática, después de medianoche del día siguiente).
 */
function GlobalInvoiceSection({ d, run, busy }: {
  d: NonNullable<IssuerStatus['data']>;
  run: (fn: () => Promise<void>) => Promise<void>;
  busy: boolean;
}) {
  const [ack, setAck] = useState(false);
  // PR A (V204) — aviso de consumo de folios: solo se pide si aún no está aceptado.
  const [folioAck, setFolioAck] = useState(false);
  // V222 (C-02): la hora va de 0 a 20; un valor viejo mayor se muestra (y guarda) como 20.
  const [runHour, setRunHour] = useState<string>(
    d.globalInvoiceRunHour == null ? '' : String(Math.min(d.globalInvoiceRunHour, GLOBAL_MAX_RUN_HOUR)));
  const enabled = d.globalInvoiceEnabled;
  const folioNoticeNeeded = !d.folioNoticeGlobalAccepted;
  // V222 (C-03): activar exige el emisor listo (mismo criterio que el timbrado).
  const missing = d.issuerMissing ?? [];
  const issuerReady = missing.length === 0;

  // 2026-08-05 (contador): SOLO Mensual. Se quitaron Diaria/Semanal del selector; el
  // bloqueo del individual globalizado + la ventana de 24h funcionan con mensual. Se
  // envía '04' fijo (normaliza a mensual a un tenant que tuviera '01'/'02' guardado).
  const save = (nextEnabled: boolean) => run(async () => {
    await api('/me/cfdi-issuer/global-invoice', {
      method: 'PUT',
      body: {
        enabled: nextEnabled,
        noticeAck: nextEnabled ? (ack || Boolean(d.globalInvoiceNoticeAckAt)) : true,
        // Al prender: acepta el aviso de folios si aún no estaba aceptado (ya aceptado → true).
        acceptFolioNotice: nextEnabled ? (folioAck || d.folioNoticeGlobalAccepted) : false,
        periodicity: '04',
        ...(runHour === '' ? {} : { runHour: Number(runHour) }),
      },
    });
  });

  const labelCls = 'text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400';
  const selectCls = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-400 focus:outline-none';
  // Selects compartidos por ambos estados (activa/inactiva). SOLO Mensual (contador 2026-08-05).
  const controls = (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <label className={labelCls}>Periodicidad</label>
          {/* Sin dropdown: mensual es la única opción (recomendación del contador). */}
          <div className={`${selectCls} bg-slate-50 text-slate-600`}>Mensual</div>
        </div>
        <div>
          <label className={labelCls}>Hora de emisión</label>
          <select value={runHour} onChange={(e) => setRunHour(e.target.value)} className={selectCls}>
            <option value="">6:00 a. m. (recomendada)</option>
            {Array.from({ length: GLOBAL_MAX_RUN_HOUR + 1 }, (_, h) => (
              <option key={h} value={h}>{String(h).padStart(2, '0')}:00 (hora del centro)</option>
            ))}
          </select>
          <p className="mt-1 text-[11px] text-slate-400">
            Configúralo para que el global se emita <strong>ANTES de la apertura del día 1 del mes</strong>
            {' '}(ej. 6:00 a. m.), para que las ventas del mes anterior queden consolidadas antes de la primera venta.
          </p>
        </div>
      </div>

      <div>
        <label className={labelCls}>Cuándo se emite el global mensual</label>
        {/* V222 (C-01): se retiró "el último día del mes": timbraba antes del cierre real y
            dejaba sin CFDI las ventas posteriores de ese día. */}
        <p className="mt-1 text-sm text-slate-700">
          El <strong>1º de cada mes</strong>, a la hora que elijas, por las ventas del <strong>mes anterior</strong>.
        </p>
      </div>
    </div>
  );

  const missingNote = (
    <p className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
      Tu emisor CFDI no está listo. Falta: <strong>{describeMissing(missing)}</strong>.
    </p>
  );

  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-slate-800">Factura global <span className="text-slate-400">(opcional)</span></p>
        {enabled && (issuerReady ? (
          <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700">
            Mensual
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-rose-50 px-2.5 py-1 text-[11px] font-medium text-rose-700">
            No se va a emitir
          </span>
        ))}
      </div>
      <p className="mt-1 text-xs text-slate-500">
        Al cerrar cada mes, las ventas que <strong>nadie facturó</strong> (ni el cajero ni el cliente por su
        cuenta) se consolidan en <strong>una sola factura global</strong> de «público en general», como pide el SAT.
        Se emite a la hora que configures abajo.
      </p>

      {enabled ? (
        <div className="mt-3 space-y-3">
          {issuerReady ? (
            <p className="text-xs text-emerald-700">
              ✓ Activada · Mensual
              {d.globalInvoiceNoticeAckAt ? ` · aviso reconocido el ${new Date(d.globalInvoiceNoticeAckAt).toLocaleDateString('es-MX')}` : ''}.
            </p>
          ) : (
            <div className="space-y-1">
              <p className="text-xs font-medium text-rose-700">
                ⚠ Activada, pero no se va a emitir: las ventas del periodo se quedan sin CFDI hasta que completes tu emisor.
              </p>
              {missingNote}
            </div>
          )}
          {controls}
          <div className="flex items-center gap-2">
            <button onClick={() => save(true)} disabled={busy}
              className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50">
              {busy ? 'Guardando…' : 'Guardar cambios'}
            </button>
            <button onClick={() => save(false)} disabled={busy}
              className="rounded-xl px-3 py-2 text-sm font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50">
              Desactivar
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5" />
            <span>
              Entiendo que <strong>se generará una factura global</strong> (según la periodicidad elegida) por las
              ventas que no se facturaron individualmente, a nombre de «público en general».
            </span>
          </label>
          {folioNoticeNeeded && (
            <label className="flex items-start gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={folioAck} onChange={(e) => setFolioAck(e.target.checked)} className="mt-0.5" />
              <span>
                Entiendo que <strong>cada factura global consume un folio</strong> de mi bolsa anual. Si se agota, el
                timbrado <strong>no se bloquea</strong>, pero recibiré avisos al acercarme al límite.
              </span>
            </label>
          )}
          {controls}
          {!issuerReady && missingNote}
          <div>
            <button onClick={() => save(true)} disabled={busy || !issuerReady || !ack || (folioNoticeNeeded && !folioAck)}
              className="rounded-xl bg-[var(--brand-primary)] px-3 py-2 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
              {busy ? 'Activando…' : 'Activar factura global'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * V118 — "Facturar al reservar" (Wellness/Boutique): toggle POR TENANT que decide si al
 * reservar/pagar una clase se le ofrece factura al cliente (QR al portal `/factura/:code`).
 * Fail-closed: solo se puede ENCENDER si el emisor está listo Y Pagos está activo
 * (`bookingInvoiceGate`); si no, "Activar" se muestra DESHABILITADO con explicación.
 * APAGAR siempre se permite, aunque falte el emisor (invoiceToggles.ts).
 * OFF por default. Guardar es inerte hasta el onboard (el QR se cablea aparte).
 */
function BookingInvoiceSection({ d, run, busy }: {
  d: NonNullable<IssuerStatus['data']>;
  run: (fn: () => Promise<void>) => Promise<void>;
  busy: boolean;
}) {
  const gate = d.bookingInvoiceGate; // emisor listo && Pagos activo
  const enabled = d.bookingInvoiceEnabled;
  // PR A (V204) — aviso de consumo de folios: solo al PRENDER y si aún no está aceptado.
  const [folioAck, setFolioAck] = useState(false);
  const folioNoticeNeeded = !d.folioNoticeBookingAccepted;
  const btn = bookingInvoiceButton({ enabled, gate, folioNoticeNeeded, folioAck, busy });

  const save = (next: boolean) => run(async () => {
    await api('/me/cfdi-issuer/booking-invoice', {
      method: 'PUT',
      body: { enabled: next, acceptFolioNotice: next ? (folioAck || d.folioNoticeBookingAccepted) : false },
    });
  });

  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-slate-800">Facturar al reservar <span className="text-slate-400">(Wellness/Boutique)</span></p>
        {enabled && gate && (
          <span className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-700">Activada</span>
        )}
      </div>
      <p className="mt-1 text-xs text-slate-500">
        Cuando un cliente reserve y pague una clase, se le ofrece su <strong>factura</strong> (QR + liga al portal de
        autofactura) en la pantalla de confirmación y en el correo. El cliente captura ahí su RFC; no se piden datos
        fiscales en el cobro.
      </p>

      {!gate && enabled && (
        <p className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Está encendida, pero no se ofrece factura mientras falte {!d.issuerEnabled ? 'el emisor CFDI' : ''}{!d.issuerEnabled && !d.chargesEnabled ? ' y ' : ''}{!d.chargesEnabled ? 'activar Pagos' : ''}. Puedes desactivarla.
        </p>
      )}
      {!gate && !enabled && (
        <p className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Necesitas activar <strong>Facturación CFDI</strong> (emisor con CSD) y <strong>Pagos</strong> para ofrecer
          factura al reservar. {!d.issuerEnabled && 'Falta el emisor CFDI. '}{!d.chargesEnabled && 'Falta activar Pagos.'}
        </p>
      )}

      {gate && !enabled && folioNoticeNeeded && (
        <label className="mt-3 flex items-start gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={folioAck} onChange={(e) => setFolioAck(e.target.checked)} className="mt-0.5" />
          <span>
            Entiendo que <strong>cada factura que se emita al reservar consume un folio</strong> de mi bolsa anual. Si se
            agota, el timbrado <strong>no se bloquea</strong>, pero recibiré avisos al acercarme al límite.
          </span>
        </label>
      )}

      <div className="mt-3">
        <button
          onClick={() => save(!enabled)}
          disabled={btn.disabled}
          className={`inline-flex min-h-10 items-center gap-2 rounded-xl px-3 text-sm font-medium disabled:opacity-50 ${
            enabled
              ? 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
              : 'bg-[var(--brand-primary)] text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)]'}`}
          title={btn.title}>
          {btn.label}
        </button>
      </div>
    </div>
  );
}

export function IssuerConfigCard() {
  const { user } = useAuth() as any;
  const isSuper = user?.role === 'SUPER_USER';
  const tenantSlug: string = user?.tenant?.slug ?? '';
  const tenantName: string = user?.tenant?.name ?? '';

  const [status, setStatus] = useState<IssuerStatus | null>(null);
  const [form, setForm] = useState({ rfc: '', razonSocial: '', regimenFiscal: '601', cp: '', serie: 'A' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  // Paso 2 (CSD): feedback INLINE propio (#568). `csdKey` remonta los inputs al
  // tener éxito (limpia los archivos seleccionados).
  const [csdErr, setCsdErr] = useState<string | null>(null);
  const [csdOk, setCsdOk] = useState<string | null>(null);
  const [csdBusy, setCsdBusy] = useState(false);
  const [csdKey, setCsdKey] = useState(0);

  const load = () =>
    api<IssuerStatus>('/me/cfdi-issuer').then((r) => {
      setStatus(r);
      if (r.data) setForm({
        rfc: r.data.rfc, razonSocial: r.data.razonSocial, regimenFiscal: r.data.regimenFiscal,
        cp: r.data.cp, serie: r.data.serie,
      });
    });

  useEffect(() => {
    if (!isSuper) return;
    load().catch(() => { /* feature dormida o sin permisos: card no se muestra */ });
  }, [isSuper]);

  // Dormida: sin kill-switch global no existe para el tenant.
  if (!isSuper || !status?.enabled) return null;

  const d = status.data;
  const flash = (m: string) => { setOk(m); setTimeout(() => setOk(null), 5000); };
  const run = async (fn: () => Promise<void>) => {
    setBusy(true); setErr(null); setOk(null);
    try { await fn(); await load(); } catch (e: any) { setErr(errMsg(e, 'No se pudo completar la operación.')); }
    finally { setBusy(false); }
  };

  // Paso 2 (CSD): maneja su propio feedback (éxito con vigencia / error del backend en
  // palabras del tenant). En éxito refresca el chip (load) y remonta los inputs (csdKey).
  const submitCsd = async (cer: File, key: File, password: string) => {
    setCsdBusy(true); setCsdErr(null); setCsdOk(null);
    try {
      const r = (await uploadCertPair('/me/cfdi-issuer/csd', cer, key, password)) as { csdValidTo?: string | null };
      await load();                       // refresca el chip "2 · CSD" con la vigencia
      setCsdOk(csdOkMessage(r?.csdValidTo));
      setCsdKey((k) => k + 1);            // limpia los archivos seleccionados
    } catch (e: any) {
      setCsdErr(errMsg(e, 'No se pudo subir el CSD. Revisa que sea el par .cer/.key (no la FIEL) y la contraseña.'));
    } finally {
      setCsdBusy(false);
    }
  };

  const inputCls = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-400 focus:outline-none';
  const labelCls = 'text-[11px] font-medium uppercase tracking-[0.18em] text-slate-400';
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm({ ...form, [k]: e.target.value });

  return (
    <div className="card-soft rounded-2xl border border-slate-200/80 bg-white">
      <div className="border-b border-slate-100 px-6 py-5 md:px-8">
        <h3 className="font-serif text-lg tracking-tight text-slate-900">Facturar a mis clientes (CFDI)</h3>
        <p className="text-sm text-slate-500">
          Emite facturas <strong>a nombre de tu negocio</strong> desde el punto de venta. Tu CSD y tu FIEL
          viajan directo al proveedor autorizado (PAC) y <strong>nunca se guardan en FluidSol</strong>.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Chip ok={Boolean(d?.personLinked)} label="1 · Identidad fiscal" />
          <Chip ok={Boolean(d?.csdVigente)} label={d?.csdValidTo ? `2 · CSD (vence ${new Date(d.csdValidTo).toLocaleDateString('es-MX')})` : '2 · CSD'} />
          <Chip ok={d?.manifestStatus === 'signed'} label="3 · Manifiesto firmado" />
          <Chip ok={Boolean(d?.contractCurrent)} label="4 · Contrato" />
          <Chip ok={Boolean(d?.privacyAttestedAt)} label="5 · Aviso de privacidad" />
          <Chip ok={Boolean(d?.issuerEnabled)} label="6 · Activado por FluidSol" />
          {status.ready && (
            <span className="inline-flex items-center rounded-full bg-emerald-600 px-2.5 py-1 text-[11px] font-semibold text-white">
              Listo para facturar
            </span>
          )}
        </div>
      </div>

      <div className="space-y-5 px-6 py-5 md:px-8">
        {/* PR A (V204) — medidor de la bolsa de folios (visible una vez activo el emisor). */}
        {d?.issuerEnabled && <FoliosUsageMeter />}
        {/* Paso 1 — identidad */}
        <div className="rounded-xl border border-slate-200 p-4">
          <p className="mb-3 text-sm font-medium text-slate-800">Paso 1 · Identidad fiscal del emisor</p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className={labelCls}>RFC</label>
              <input value={form.rfc} onChange={set('rfc')} maxLength={13}
                placeholder="AAA010101AAA" className={`${inputCls} font-mono uppercase`} />
            </div>
            <div>
              <label className={labelCls}>Código postal (lugar de expedición)</label>
              <input value={form.cp} onChange={set('cp')} maxLength={5} inputMode="numeric"
                placeholder="00000" className={`${inputCls} font-mono`} />
            </div>
            <div className="md:col-span-2">
              <label className={labelCls}>Razón social</label>
              <input value={form.razonSocial} onChange={set('razonSocial')}
                placeholder="Sin régimen societario (sin S.A. de C.V.)" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Régimen fiscal</label>
              <select value={form.regimenFiscal} onChange={set('regimenFiscal')} className={inputCls}>
                {(status.catalogs?.regimenesFiscales ?? Object.keys(REGIMEN_LABELS)).map((c) => (
                  <option key={c} value={c}>{REGIMEN_LABELS[c] ?? c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Serie de facturación</label>
              <input value={form.serie} onChange={set('serie')} maxLength={10}
                className={`${inputCls} font-mono uppercase`} />
            </div>
          </div>
          <div className="mt-3 flex justify-end">
            <button
              onClick={() => run(async () => {
                await api('/me/cfdi-issuer', { method: 'PUT', body: form });
                flash('Identidad guardada y registrada con el PAC ✓');
              })}
              disabled={busy}
              className="rounded-xl bg-[var(--brand-primary)] px-3 py-2 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
              {busy ? 'Guardando…' : 'Guardar identidad'}
            </button>
          </div>
        </div>

        {/* Paso 2 — CSD (feedback INLINE propio: submitCsd + csdErr/csdOk/csdBusy; #568). */}
        {d?.personLinked && (
          <CertPairInputs
            key={csdKey}
            label="Paso 2 · Certificado de Sello Digital (CSD)"
            hint="Es el par .cer/.key para FACTURAR (no la FIEL). Se envía directo al PAC; FluidSol no guarda los archivos ni la contraseña."
            cta={d.csdStatus === 'uploaded' ? 'Reemplazar CSD' : 'Subir CSD'}
            busy={csdBusy}
            error={csdErr}
            okMsg={csdOk}
            onSubmit={submitCsd}
          />
        )}

        {/* Paso 3 — manifiesto (mecanismo 3: portal hospedado; la e.firma NUNCA toca FluidSol).
            Se firma en el portal de FiscalAPI y aquí solo VERIFICAMOS por lectura del PAC. */}
        {d?.personLinked && d.csdStatus === 'uploaded' && (
          <div className="rounded-xl border border-slate-200 p-4">
            <p className="mb-1 text-sm font-medium text-slate-800">Paso 3 · Firma del manifiesto (Carta Manifiesto)</p>
            {d.manifestStatus === 'signed' ? (
              <p className="text-sm text-emerald-700">✓ Manifiesto firmado (verificado con el PAC).</p>
            ) : (
              <>
                <p className="mb-3 text-xs text-slate-500">
                  El SAT exige autorizar al PAC para timbrar a tu nombre (Carta Manifiesto, reglas 2.7.2.5 y
                  2.7.2.7). La firmas <strong>con tu e.firma en el portal de FiscalAPI</strong> (te compartimos
                  el link); tu e.firma <strong>nunca pasa por FluidSol</strong>. Cuando la hayas firmado, pulsa
                  «Verificar firma».
                </p>
                <div className="flex justify-end">
                  <button
                    onClick={() => run(async () => {
                      const r = await api('/me/cfdi-issuer/manifest/verify', { method: 'POST', body: {} }) as { signed?: boolean };
                      flash(r?.signed
                        ? 'Manifiesto firmado ✓ (verificado con el PAC).'
                        : 'Aún no aparece firmado en el PAC. Firma en el portal y vuelve a verificar.');
                    })}
                    disabled={busy}
                    className="rounded-xl bg-[var(--brand-primary)] px-3 py-2 text-sm font-medium text-[var(--brand-primary-fg)] hover:bg-[var(--brand-primary-hover)] disabled:opacity-50">
                    {busy ? 'Verificando…' : 'Verificar firma'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Paso 4/5 — gate legal (V98): contrato + attestation del aviso + aviso hospedado (F) */}
        {d?.personLinked && (
          <LegalGateSection d={d} slug={tenantSlug} tenantName={tenantName} run={run} busy={busy} />
        )}

        {/* PR2/D — opt-in de la factura global diaria (una vez configurada la identidad). */}
        {d && showInvoiceToggle(d.personLinked, d.globalInvoiceEnabled) && <GlobalInvoiceSection d={d} run={run} busy={busy} />}
        {d && showBookingInvoice(d.bookingInvoiceAvailable, d.bookingInvoiceEnabled)
          && showInvoiceToggle(d.personLinked, d.bookingInvoiceEnabled) && <BookingInvoiceSection d={d} run={run} busy={busy} />}

        {d && !d.issuerEnabled && d.manifestStatus === 'signed' && d.contractSignedAt && d.privacyAttestedAt && (
          <p className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Todo listo de tu lado. FluidSol validará tu contrato y la confirmación de tu aviso de
            privacidad para activar la facturación.
          </p>
        )}

        {err && <p className="text-sm text-rose-600">{err}</p>}
        {ok && <p className="text-sm text-emerald-600">{ok}</p>}
      </div>
    </div>
  );
}
